import React, { useMemo } from 'react';
import { useCfoCoverageHeatmap } from '../../../hooks/analytics/useCfoReports.ts';
import { WidgetCard, formatNumber } from '../controller/shared.tsx';

function heatColor(pct: number): string {
  if (pct >= 90) return 'bg-emerald-600 text-white';
  if (pct >= 75) return 'bg-emerald-400 text-white';
  if (pct >= 50) return 'bg-amber-300 text-amber-950';
  if (pct >= 25) return 'bg-orange-300 text-orange-950';
  return 'bg-rose-300 text-rose-950';
}

/**
 * Report #2 — Control Coverage Heatmap. The backend row is one cell per SOX
 * cycle (controls / active_controls, no second dimension), so this renders a
 * colour-coded grid of cycles sized by active-control coverage rather than a
 * true 2-D heatmap.
 */
export const CoverageHeatmapGrid: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCfoCoverageHeatmap();
  const rows = data ?? [];

  const cells = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        pct: row.controls > 0 ? (100 * row.active_controls) / row.controls : 0,
      })),
    [rows]
  );

  return (
    <WidgetCard
      title="Control Coverage Heatmap"
      description="Active vs. total controls per SOX cycle"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load coverage heatmap.'}
      isEmpty={!isLoading && !isError && cells.length === 0}
      emptyMessage="No SOX cycles found."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cells.map((cell) => (
          <div
            key={cell.cycle}
            className={`rounded-xl p-4 flex flex-col justify-between shadow-sm ${heatColor(cell.pct)}`}
          >
            <p className="text-xs font-semibold uppercase tracking-wide opacity-90 truncate">{cell.cycle}</p>
            <p className="analytics-kpi-value text-2xl font-bold mt-2">{cell.pct.toFixed(0)}%</p>
            <p className="text-xs opacity-90 mt-1 tabular-nums">
              {formatNumber(cell.active_controls)} / {formatNumber(cell.controls)} active
            </p>
          </div>
        ))}
      </div>
    </WidgetCard>
  );
};

export default CoverageHeatmapGrid;
